import { useState, useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useQueryClient } from '@tanstack/react-query';
import { authService, AttributeList } from './AuthService';
import useAppDispatch from '../redux/useAppDispatch';
import accountSlice, { deleteAccountThunk } from './slice';
import getErrorMessage from './getErrorMessage';
import {
  AuthenticationMethod,
  AuthenticationStep,
  CognitoAuthProvider,
  CustomAuthProvider,
  Locale,
} from '../constants';
import bookingSlice from '../booking/slice';
import analytics from '../analytics/analytics';
import CognitoError from '../error/CognitoError';
import UnauthorizedError from '../error/UnauthorizedError';
import socialSlice from '../social/slice';
import { useSelector } from '../redux/store';

export const useSignUp = (): [
  typeof signUpFunc,
  boolean,
  string | undefined,
  string | undefined,
] => {
  const router = useRouter();
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>();
  const [code, setCode] = useState<string>();
  const { redirect } = router.query as {
    redirect?: string;
  };

  const signUpFunc = (
    firstNameKanji: string,
    lastNameKanji: string,
    email: string,
    password: string,
    callback?: (email: string) => void
  ) => {
    setLoading(true);
    authService
      .signUp(firstNameKanji, lastNameKanji, email, password, router.locale)
      .then(() => {
        if (callback) {
          callback(email);
        } else {
          const parameter = new URLSearchParams({
            email,
            redirect: redirect || '',
          });
          router.push(`/account/signup-confirmation?${parameter.toString()}`);
        }
        analytics.google.basic.track.signup(AuthenticationMethod.COGNITO);
      })
      .catch((error) => {
        if (error instanceof CognitoError) {
          setCode(error.code);
          setMessage(getErrorMessage(error.code, error.message));
        }
        setLoading(false);
      });
  };

  return [signUpFunc, loading, code, message];
};

export const useResendSignUpConfirmation = (): [
  typeof resendSignUpConfirmationFunction,
  boolean,
] => {
  const router = useRouter();
  const [loading, setLoading] = useState<boolean>(false);
  const resendSignUpConfirmationFunction = async (email: string): Promise<boolean> => {
    setLoading(true);
    try {
      await authService.resendSignUpConfirmation(email, router.locale);
      return true;
    } catch (error) {
      console.error(error);
      setLoading(false);
      return false;
    }
  };

  return [resendSignUpConfirmationFunction, loading];
};

export const useSignIn = (
  redirect?: string,
  confirmSignUpCallback?: (email: string) => void
): [typeof signInFunc, boolean, Record<string, string | undefined> | undefined] => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<Record<string, string | undefined>>();
  const signInFunc = async (username: string, password: string) => {
    setLoading(true);

    try {
      dispatch(socialSlice.actions.clearSocialProvider());
      const nextStep = await authService.signIn(username, password);

      // Redirect user to /signup-confirmation when user try to login but account is not verified yet.
      if (nextStep === 'CONFIRM_SIGN_UP') {
        if (confirmSignUpCallback) {
          confirmSignUpCallback(username);
        } else {
          const parameter = new URLSearchParams({
            email: username,
            redirect: redirect || '',
          });
          await router.push(`/account/signup-confirmation?${parameter.toString()}`);
        }
        return;
      }

      const { userPayload } = authService;

      const locale = ((Object.values(Locale).includes(userPayload?.locale as string) &&
        userPayload?.locale) ||
        router?.locale ||
        Locale.JAPANESE) as string;

      dispatch(
        accountSlice.actions.setUserAttributes({
          accountId: userPayload?.accountId || '',
          given_name: userPayload?.given_name || '',
          family_name: userPayload?.family_name || '',
          email: userPayload?.email || '',
          email_verified: userPayload?.email_verified || '',
          'custom:given_name_kana': userPayload?.['custom:given_name_kana'] || '',
          'custom:family_name_kana': userPayload?.['custom:family_name_kana'] || '',
          'custom:phone': userPayload?.['custom:phone'] || '',
          gender: userPayload?.gender || '',
          'custom:country': userPayload?.['custom:country'] || '',
          'custom:postal_code': userPayload?.['custom:postal_code'] || '',
          'custom:region': userPayload?.['custom:region'] || '',
          'custom:address': userPayload?.['custom:address'] || '',
          locale,
        })
      );

      // Overwrite bookingSlice by userPayload when user sign-in during booking.
      dispatch(
        bookingSlice.actions.updateCustomer({
          firstNameKanji: userPayload?.given_name || '',
          lastNameKanji: userPayload?.family_name || '',
          email: userPayload?.email || '',
          firstNameKana: userPayload?.['custom:given_name_kana'] || '',
          lastNameKana: userPayload?.['custom:family_name_kana'] || '',
          phone: userPayload?.['custom:phone'] || '',
          country: userPayload?.['custom:country'] || '',
          postalCode: userPayload?.['custom:postal_code'] || '',
          region: userPayload?.['custom:region'] || '',
          address: userPayload?.['custom:address'] || '',
          specialRequest: '',
          isEmailVerified: userPayload?.email_verified === 'true',
        })
      );

      if (userPayload?.['custom:a8_parameter_id']) {
        dispatch(
          bookingSlice.actions.setAffiliateParameter({
            a8: {
              id: userPayload?.['custom:a8_parameter_id'],
              expiry: Number(userPayload?.['custom:a8_parameter_expiry']) || 0,
            },
          })
        );
      }
      if (
        redirect ||
        (router.query.redirect &&
          /^(?!\/account\/(details|my-trips|my-coupons|update-email)\/).*$/.test(
            router.query.redirect as string
          ))
      ) {
        await router.push(redirect || (router.query?.redirect as string), undefined, { locale });
      } else {
        await router.push('/', undefined, { locale });
      }
      analytics.google.basic.track.login(AuthenticationMethod.COGNITO);
    } catch (error) {
      if (error instanceof CognitoError) {
        setLoading(false);
        if (error.code === 'InvalidParameterException') {
          setMessage({
            email: getErrorMessage(error.code, error.message || JSON.stringify(error)),
          });
        } else {
          setMessage({
            password: getErrorMessage(error.code, error.message || JSON.stringify(error)),
          });
        }
      }
    }
  };

  return [signInFunc, loading, message];
};

export const useSignInWithProvider = (
  step: AuthenticationStep,
  redirect?: string
): [typeof signInFunc, Record<string, string | undefined> | undefined] => {
  const dispatch = useAppDispatch();
  const [message, setMessage] = useState<Record<string, string | undefined>>();
  const signInFunc = async (provider: CognitoAuthProvider | CustomAuthProvider) => {
    try {
      dispatch(
        socialSlice.actions.setSocialProvider({
          type: provider,
          redirect,
          step,
        })
      );
      await authService.signInWithProvider(provider, redirect);
      analytics.google.basic.track.login(provider);
    } catch (error) {
      if (error instanceof CognitoError) {
        if (error.code === 'InvalidParameterException') {
          setMessage({
            email: getErrorMessage(error.code, error.message || JSON.stringify(error)),
          });
        } else {
          setMessage({
            password: getErrorMessage(error.code, error.message || JSON.stringify(error)),
          });
        }
      }
    }
  };

  return [signInFunc, message];
};

export const useForgotPassword = (): [typeof forgotPasswordFunc, boolean, string | undefined] => {
  const router = useRouter();
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>();
  const { redirect } = router.query as {
    redirect?: string;
  };
  const forgotPasswordFunc = async (email: string) => {
    setLoading(true);
    try {
      await authService.forgotPassword(email, router.locale, redirect);
      const parameter = new URLSearchParams({
        email,
        redirect: redirect || '',
      });
      await router.push(
        router.pathname === '/account/password-reset-invalid'
          ? `/account/password-reset-resent?${parameter.toString()}`
          : `/account/password-reset-sent?${parameter.toString()}`
      );
    } catch (error) {
      setLoading(false);
      if (error instanceof CognitoError) {
        setMessage(getErrorMessage(error.code, error.message || JSON.stringify(error)));
      }
    }
  };

  return [forgotPasswordFunc, loading, message];
};

export const useConfirmPassword = (): [typeof confirmPasswordFunc, boolean, string | undefined] => {
  const router = useRouter();
  const { redirect } = router.query as {
    redirect?: string;
  };
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>();
  const confirmPasswordFunc = async (email: string, resetCode: string, newPassword: string) => {
    setLoading(true);
    const parameter = new URLSearchParams({
      email,
      redirect: redirect || '',
    });

    try {
      await authService.confirmPassword(email, resetCode, newPassword);
      await router.push(`/account/login?${parameter.toString()}`);
    } catch (error) {
      if (error instanceof CognitoError && error.code === 'ExpiredCodeException') {
        await router.push(`/account/password-reset-invalid?${parameter.toString()}`);
      } else if (error instanceof CognitoError) {
        setLoading(false);
        setMessage(getErrorMessage(error.code, error.message || JSON.stringify(error)));
      }
    }
  };

  return [confirmPasswordFunc, loading, message];
};

export const useConfirmSignUp = (): [typeof confirmSignUpFunc, boolean, string | undefined] => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const [loading, setLoading] = useState<boolean>(false); // the initial value is true so that the success screen is not shown while loading
  const [message, setMessage] = useState<string>();

  const confirmSignUpFunc = useCallback(
    async (
      email: string,
      code: string,
      redirect?: string,
      callback?: (isLoggedIn: boolean) => void
      // eslint-disable-next-line consistent-return
    ): Promise<boolean | void> => {
      setLoading(true);
      try {
        const userPayload = await authService.confirmSignUp(email, code);
        const locale = ((Object.values(Locale).includes(userPayload?.locale as string) &&
          userPayload?.locale) ||
          router?.locale ||
          Locale.JAPANESE) as string;

        // Auto sign in flow not triggered
        if (!userPayload) {
          // Trigger callback so LoginModal/SignUpModal will redirect to LoginCard due to user not logged in
          if (callback) {
            return callback(false);
          }
          return await router.push(
            `/account/login?${new URLSearchParams({
              redirect: redirect || (router.query?.redirect as string),
              email,
            }).toString()}`
          );
        }

        dispatch(
          accountSlice.actions.setUserAttributes({
            accountId: userPayload?.accountId || '',
            given_name: userPayload?.given_name || '',
            family_name: userPayload?.family_name || '',
            email: userPayload?.email || '',
            email_verified: userPayload?.email_verified || '',
            'custom:given_name_kana': userPayload?.['custom:given_name_kana'] || '',
            'custom:family_name_kana': userPayload?.['custom:family_name_kana'] || '',
            'custom:phone': userPayload?.['custom:phone'] || '',
            gender: userPayload?.gender || '',
            'custom:country': userPayload?.['custom:country'] || '',
            'custom:postal_code': userPayload?.['custom:postal_code'] || '',
            'custom:region': userPayload?.['custom:region'] || '',
            'custom:address': userPayload?.['custom:address'] || '',
            locale,
          })
        );

        // Overwrite bookingSlice by userPayload when user sign-in during booking.
        dispatch(
          bookingSlice.actions.updateCustomer({
            firstNameKanji: userPayload?.given_name || '',
            lastNameKanji: userPayload?.family_name || '',
            email: userPayload?.email || '',
            firstNameKana: userPayload?.['custom:given_name_kana'] || '',
            lastNameKana: userPayload?.['custom:family_name_kana'] || '',
            phone: userPayload?.['custom:phone'] || '',
            country: userPayload?.['custom:country'] || '',
            postalCode: userPayload?.['custom:postal_code'] || '',
            region: userPayload?.['custom:region'] || '',
            address: userPayload?.['custom:address'] || '',
            specialRequest: '',
            isEmailVerified: userPayload?.email_verified === 'true',
          })
        );

        if (userPayload?.['custom:a8_parameter_id']) {
          dispatch(
            bookingSlice.actions.setAffiliateParameter({
              a8: {
                id: userPayload?.['custom:a8_parameter_id'],
                expiry: Number(userPayload?.['custom:a8_parameter_expiry']) || 0,
              },
            })
          );
        }

        if (
          redirect ||
          (router.query.redirect &&
            /^(?!\/account\/(details|my-trips|my-coupons|update-email)\/).*$/.test(
              router.query.redirect as string
            ))
        ) {
          await router.push(redirect || (router.query?.redirect as string), undefined, { locale });
        } else {
          await router.push('/', undefined, { locale });
        }
        analytics.google.basic.track.login(AuthenticationMethod.COGNITO);
        // The use will click a button to go to the login page
      } catch (error) {
        if (error instanceof CognitoError) {
          setLoading(false);
          setMessage(getErrorMessage(error.code, error.message || JSON.stringify(error)));
        }
        return false;
      }
    },
    [dispatch, router]
  );

  return [confirmSignUpFunc, loading, message];
};

export const useLogout = (): [typeof logoutFunc, boolean] => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { provider } = useSelector((state) => state.social);
  const [loading, setLoading] = useState<boolean>(false);

  const logoutFunc = () => {
    setLoading(true);
    // Set logout redirect path if user is currently logged in with SSO
    if (provider.type) {
      dispatch(
        socialSlice.actions.setSocialProvider({
          type: provider.type,
          step: AuthenticationStep.LOGOUT,
          redirect: router.asPath,
        })
      );
    }
    authService
      .logout()
      .then(() => {
        dispatch(accountSlice.actions.clearUserAttributes());
        dispatch(bookingSlice.actions.resetCustomer());
        dispatch(bookingSlice.actions.resetAffiliateParameter());
        queryClient.clear();
        // Redirect user back to customer detail page if they were previously on payment selection page
        if (router.pathname === '/b/[hotel]/payment-selection') {
          router.push(router.asPath.replace('/payment-selection', '/customer-detail'));
        } else {
          router.push(router.asPath);
        }
      })
      .catch(() => setLoading(false));
  };

  return [logoutFunc, loading];
};

export const useDeleteAccount = (): [typeof deleteAccountFunc, boolean] => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState<boolean>(false);
  const { provider } = useSelector((state) => state.social);

  const deleteAccountFunc = async () => {
    setLoading(true);
    const userSession = await authService.getSession();
    if (!userSession?.tokens?.accessToken) {
      router.push('/');
      return;
    }

    const accountId = await authService.getAccount(userSession);
    if (accountId) {
      const body = {
        accountId,
        idToken: userSession?.tokens?.accessToken?.toString() as string,
      };
      await dispatch(deleteAccountThunk(body));
      if (provider?.type) {
        dispatch(
          socialSlice.actions.setSocialProvider({
            type: provider?.type,
            step: AuthenticationStep.DELETE_ACCOUNT,
            redirect: '/?account-deleted=true',
          })
        );
      }
      authService
        .logout()
        .then(() => {
          dispatch(accountSlice.actions.clearUserAttributes());
          dispatch(bookingSlice.actions.resetCustomer());
          dispatch(bookingSlice.actions.resetAffiliateParameter());
          queryClient.clear();
          router.push('/?account-deleted=true');
        })
        .catch(() => setLoading(false));
    }
  };

  return [deleteAccountFunc, loading];
};

export const useUpdateAttributes = (): [typeof updateAttributesFunc, boolean] => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const [loading, setLoading] = useState<boolean>(false);

  const updateAttributesFunc = async (attributeList: AttributeList) => {
    setLoading(true);
    try {
      await authService.updateAttributes(attributeList, router.locale);

      // update redux with the new user attributes
      const { userPayload } = authService;

      dispatch(
        accountSlice.actions.setUserAttributes({
          accountId: userPayload?.accountId || '',
          given_name: userPayload?.given_name || '',
          family_name: userPayload?.family_name || '',
          email: userPayload?.email || '',
          email_verified: userPayload?.email_verified || '',
          gender: userPayload?.gender || '',
          locale: ((Object.values(Locale).includes(userPayload?.locale as string) &&
            userPayload?.locale) ||
            Locale.JAPANESE) as string,
          'custom:given_name_kana': userPayload?.['custom:given_name_kana'] || '',
          'custom:family_name_kana': userPayload?.['custom:family_name_kana'] || '',
          'custom:phone': userPayload?.['custom:phone'] || '',
          'custom:country': userPayload?.['custom:country'] || '',
          'custom:postal_code': userPayload?.['custom:postal_code'] || '',
          'custom:region': userPayload?.['custom:region'] || '',
          'custom:address': userPayload?.['custom:address'] || '',
        })
      );

      dispatch(
        bookingSlice.actions.updateCustomer({
          firstNameKanji: userPayload?.given_name || '',
          lastNameKanji: userPayload?.family_name || '',
          email: userPayload?.email || '',
          firstNameKana: userPayload?.['custom:given_name_kana'] || '',
          lastNameKana: userPayload?.['custom:family_name_kana'] || '',
          phone: userPayload?.['custom:phone'] || '',
          country: userPayload?.['custom:country'] || '',
          postalCode: userPayload?.['custom:postal_code'] || '',
          region: userPayload?.['custom:region'] || '',
          address: userPayload?.['custom:address'] || '',
          specialRequest: '',
          isEmailVerified: userPayload?.email_verified === 'true',
        })
      );

      if (userPayload?.['custom:a8_parameter_id']) {
        dispatch(
          bookingSlice.actions.setAffiliateParameter({
            a8: {
              id: userPayload?.['custom:a8_parameter_id'],
              expiry: Number(userPayload?.['custom:a8_parameter_expiry']) || 0,
            },
          })
        );
      }
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return [updateAttributesFunc, loading];
};

export const useUpdateEmail = (): [typeof updateEmailFunc, boolean, string | undefined] => {
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>();
  const dispatch = useAppDispatch();
  const router = useRouter();

  const updateEmailFunc = async (newEmail: string, currentPassword: string) => {
    setLoading(true);

    try {
      await authService.updateEmail(newEmail, currentPassword, router.locale);

      // update redux with the new user attributes
      const { userPayload } = authService;

      dispatch(
        accountSlice.actions.setEmail({
          email: userPayload?.email,
          email_verified: userPayload?.email_verified,
        })
      );
      setLoading(false);
    } catch (error) {
      if (error instanceof CognitoError) {
        setMessage(getErrorMessage(error.code, error.message || JSON.stringify(error)));
      }
      setLoading(false);
      throw error;
    }
  };

  return [updateEmailFunc, loading, message];
};

export const useConfirmUpdateEmail = (): [
  typeof confirmUpdateEmailFunc,
  boolean,
  Error | UnauthorizedError | CognitoError | string | undefined,
] => {
  const [loading, setLoading] = useState<boolean>(true); // the initial value is true so that the success screen is not shown while loading
  const [message, setMessage] = useState<Error | string>();
  const dispatch = useAppDispatch();
  const router = useRouter();

  const confirmUpdateEmailFunc = useCallback(
    async (code: string) => {
      setLoading(true);

      try {
        await authService.confirmUpdateEmail(code);
        const { userPayload } = authService;

        dispatch(
          bookingSlice.actions.updateCustomer({
            firstNameKanji: userPayload?.given_name || '',
            lastNameKanji: userPayload?.family_name || '',
            email: userPayload?.email || '',
            firstNameKana: userPayload?.['custom:given_name_kana'] || '',
            lastNameKana: userPayload?.['custom:family_name_kana'] || '',
            phone: userPayload?.['custom:phone'] || '',
            country: userPayload?.['custom:country'] || '',
            postalCode: userPayload?.['custom:postal_code'] || '',
            region: userPayload?.['custom:region'] || '',
            address: userPayload?.['custom:address'] || '',
            specialRequest: '',
            isEmailVerified: userPayload?.email_verified === 'true',
          })
        );

        await router.push('/account/update-email-confirmed');
      } catch (error) {
        if (error instanceof UnauthorizedError) {
          await router.push(
            `/account/login?${new URLSearchParams({
              redirect: router.asPath || '',
            }).toString()}`
          );
        } else if (error instanceof CognitoError) {
          setMessage(
            getErrorMessage(
              (error as CognitoError)?.code,
              (error as CognitoError)?.message || JSON.stringify(error)
            )
          );
        } else {
          setMessage(error as Error);
        }
        setLoading(false);
      }
    },
    [dispatch, router]
  );

  return [confirmUpdateEmailFunc, loading, message];
};

export const useUpdatePassword = (): [typeof updatePasswordFunc, boolean, string | undefined] => {
  const [updatePasswordLoading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>();
  const updatePasswordFunc = async (oldPassword: string, newPassword: string) => {
    setLoading(true);

    try {
      await authService.updatePassword(oldPassword, newPassword);
    } catch (error) {
      // error handling stuff
      if (error instanceof CognitoError) {
        setMessage(getErrorMessage(error.code, error.message || JSON.stringify(error)));
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return [updatePasswordFunc, updatePasswordLoading, message];
};

export const useInitAuth = (callback?: () => void): void => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const path = router.pathname;
  // TODO set loading status where getting cognito information to render splash screen

  const init = useCallback(async () => {
    try {
      await authService.init();
      const { userPayload } = authService;

      const locale = ((Object.values(Locale).includes(userPayload?.locale as string) &&
        userPayload?.locale) ||
        router?.locale ||
        Locale.JAPANESE) as string;

      dispatch(
        accountSlice.actions.setUserAttributes({
          accountId: userPayload?.accountId || '',
          given_name: userPayload?.given_name || '',
          family_name: userPayload?.family_name || '',
          email: userPayload?.email || '',
          email_verified: userPayload?.email_verified || '',
          gender: userPayload?.gender || '',
          locale,
          'custom:given_name_kana': userPayload?.['custom:given_name_kana'] || '',
          'custom:family_name_kana': userPayload?.['custom:family_name_kana'] || '',
          'custom:phone': userPayload?.['custom:phone'] || '',
          'custom:country': userPayload?.['custom:country'] || '',
          'custom:postal_code': userPayload?.['custom:postal_code'] || '',
          'custom:region': userPayload?.['custom:region'] || '',
          'custom:address': userPayload?.['custom:address'] || '',
        })
      );

      dispatch(accountSlice.actions.setLoggedIn());

      if (userPayload?.['custom:a8_parameter_id']) {
        dispatch(
          bookingSlice.actions.setAffiliateParameter({
            a8: {
              id: userPayload?.['custom:a8_parameter_id'],
              expiry: Number(userPayload?.['custom:a8_parameter_expiry']) || 0,
            },
          })
        );
      }

      if (callback) {
        callback();
      }

      const { pathname, asPath, query } = router;
      if (router?.query?.redirect) {
        router.push(router.query.redirect as string, undefined, { locale });
      } else if (router.locale !== locale && !router.isPreview) {
        router.push({ pathname, query }, asPath, { locale });
      }
    } catch (error) {
      console.error(error);
      // redirect the user login page with redirect parameter if they try to access a page that relies on being logged in
      if (/account\/(details|my-trips|my-coupons|update-email-confirming)/.test(path)) {
        router.push(
          `/account/login?${new URLSearchParams({
            redirect: router.asPath || '',
          }).toString()}`
        );
      }
    }
  }, [callback, dispatch, path, router]);

  // Get user from local storage everytime route is changed
  useEffect(() => {
    if (router && path && router?.isReady) {
      (async () => {
        await init();
        dispatch(accountSlice.actions.setUserInitialized());
      })();
    }
  }, [dispatch, init, path, router]);
};
